import { useTranslation } from '../../../shared/i18n';
import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Button, Tag, Space, message, Divider, Badge, Typography, Alert, Spin, Empty, Select } from 'antd';
import { ShoppingCartOutlined, SafetyOutlined, CheckCircleOutlined, CodeOutlined, FileProtectOutlined } from '@ant-design/icons';
import sslService from '../../services/sslService';
import { cartService } from '../../services/cartService';
import { useNavigate } from 'react-router-dom';

import { userPath } from '../../../shared/config/paths';

const { Title, Text, Paragraph } = Typography;

const CodeSigningBrowse = () => {
  const { t } = useTranslation();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [yearsById, setYearsById] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      // Fetch code signing products (category filter)
      const response = await sslService.getCodeSigningProducts();
      if (response.success) {
        setProducts(response.data || []);
      }
    } catch (error) {
      message.error(t('Failed to load product'));
    } finally {
      setLoading(false);
    }
  };

  // 固定年限（≥2 年）按目录卖；其余可选 1-3 年
  const canPickYears = (product) => (product.validity_period_days || 365) < 700;
  const yearsOf = (product) => (canPickYears(product) ? (yearsById[product.id] || 1) : 1);

  const addSsl = async (product) => {
    const r = await cartService.addItem({ product_id: product.id, billing_cycle: 'onetime', quantity: 1, years: yearsOf(product) });
    if (r && r.success === false) throw r;
    window.dispatchEvent(new CustomEvent('cart:updated'));
  };

  const handleAddToCart = async (product) => {
    try {
      await addSsl(product);
      message.success(t('Added to cart'));
    } catch (error) {
      message.error(t('Failed to add: {{error}}', { error: error.error || error.message || t('Unknown error') }));
    }
  };

  const handleBuyNow = async (product) => {
    try {
      await addSsl(product);
      navigate(userPath('/billing/cart'));
    } catch (error) {
      message.error(t('Operation failed: {{error}}', { error: error.error || error.message || t('Unknown error') }));
    }
  };

  const getTypeColor = (type) => {
    const colors = {
      'code_signing': 'blue',
      'ev_code_signing': 'gold',
      'document_signing': 'green',
      'smime': 'purple',
    };
    return colors[type] || 'default';
  };

  const getTypeLabel = (type) => {
    const labels = {
      'code_signing': t('Code signing'),
      'ev_code_signing': t('EV code signing'),
      'document_signing': t('Document signing'),
      'smime': 'S/MIME',
    };
    return labels[type] || type;
  };

  const getTypeIcon = (type) => {
    if (type === 'document_signing') {
      return <FileProtectOutlined />;
    }
    return <CodeOutlined />;
  };

  const getTypeFeatures = (type) => {
    const features = {
      'code_signing': [t('Software signing'), t('Driver signing'), t('OV Organization Validation'), t('SimplySign platform')],
      'ev_code_signing': [t('EV Extended Validation'), t('SmartScreen instant trust'), t('Highest security level'), t('SimplySign platform')],
      'document_signing': [t('PDF signing'), t('Document integrity'), t('Legal validity'), t('SimplySign platform')],
      'smime': [t('Email encryption'), t('Email signature'), t('Authentication')],
    };
    return features[type] || [];
  };

  const categories = [
    { key: 'all', label: t('All') },
    { key: 'Code Signing', label: t('Standard code signing') },
    { key: 'EV Code Signing', label: t('EV code signing') },
  ];

  const filteredProducts = selectedCategory === 'all'
    ? products
    : products.filter(p => p.type === selectedCategory || p.category === selectedCategory);

  return (
    <div>
      <Title level={3}>
        <CodeOutlined /> {t('Code signing certificate')}
      </Title>
      <Paragraph type="secondary">
        {t('Digitally sign your software, applications, and drivers to build user trust and meet platform requirements.')}
      </Paragraph>

      <Alert
        message={t("What is code signing?")}
        description={
          <div>
            {t('Code signing certificates digitally sign software, applications, and drivers to verify that the code comes from a trusted publisher and has not been tampered with.')}
            <ul style={{ marginTop: 8, marginBottom: 0 }}>
              <li><strong>{t('Standard code signing:')}</strong>{t('Verify publisher identity to remove the "Unknown publisher" warning')}</li>
              <li><strong>{t('EV code signing:')}</strong>{t('Extended validation establishes Microsoft SmartScreen reputation immediately, without accumulating downloads')}</li>
              <li><strong>{t('Document signing:')}</strong>{t('Digital signatures for PDF and other documents')}</li>
            </ul>
          </div>
        }
        type="info"
        showIcon
        style={{ marginBottom: 24 }}
      />

      <Space wrap style={{ marginBottom: 16 }}>
        {categories.map(cat => (
          <Button
            key={cat.key}
            type={selectedCategory === cat.key ? 'primary' : 'default'}
            onClick={() => setSelectedCategory(cat.key)}
          >
            {cat.label}
          </Button>
        ))}
      </Space>

      <Divider />

      <Spin spinning={loading}>
      <Row gutter={[16, 16]}>
        {filteredProducts.map((product) => (
          <Col xs={24} sm={12} lg={8} key={product.id}>
            <Card
              hoverable
              loading={loading}
              actions={[
                <Button
                  type="link"
                  icon={<ShoppingCartOutlined />}
                  onClick={() => handleAddToCart(product)}
                >
                  {t('Add to cart')}
                </Button>,
                <Button
                  type="primary"
                  onClick={() => handleBuyNow(product)}
                >
                  {t('Buy now')}
                </Button>,
              ]}
            >
              <Card.Meta
                avatar={
                  <Badge count={getTypeIcon(product.type || product.category)}>
                    <SafetyOutlined style={{ fontSize: 32, color: '#1890ff' }} />
                  </Badge>
                }
                title={
                  <Space>
                    {product.name}
                    <Tag color={getTypeColor(product.type || product.category)}>
                      {getTypeLabel(product.type || product.category)}
                    </Tag>
                  </Space>
                }
                description={
                  <div>
                    <div style={{ marginBottom: 8 }}>
                      <Tag>{product.brand || 'Certum'}</Tag>
                      <Tag>{product.validity_period_days ? t('{{years}} years', { years: Math.round(product.validity_period_days / 365) }) : t('1 year')}</Tag>
                    </div>
                    <div style={{ marginBottom: 12 }}>
                      {getTypeFeatures(product.type || product.category).map((feature, idx) => (
                        <div key={idx}>
                          <CheckCircleOutlined style={{ color: '#52c41a', marginRight: 4 }} />
                          <Text type="secondary">{feature}</Text>
                        </div>
                      ))}
                    </div>
                    <Space>
                      <Text strong style={{ fontSize: 18, color: '#1890ff' }}>
                        {product.currency || ''} {product.sale_price != null ? Math.round(Number(product.sale_price) * yearsOf(product) * 100) / 100 : '—'}
                      </Text>
                      {canPickYears(product) ? (
                        <Select
                          size="small"
                          value={yearsOf(product)}
                          onChange={(v) => setYearsById((m) => ({ ...m, [product.id]: v }))}
                          options={[1, 2, 3].map((y) => ({ value: y, label: t('{{n}} yr', { n: y }) }))}
                          style={{ width: 80 }}
                        />
                      ) : (
                        <Text type="secondary">/{t('{{years}} years', { years: Math.round(product.validity_period_days / 365) })}</Text>
                      )}
                    </Space>
                  </div>
                }
              />
            </Card>
          </Col>
        ))}
      </Row>

      {filteredProducts.length === 0 && !loading && (
        <Card>
          <Empty description={t("No code signing products available")} />
        </Card>
      )}
      </Spin>
    </div>
  );
};

export default CodeSigningBrowse;
